import * as moment from "moment-timezone";
import "moment/locale/es";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { Incident } from "../../models/Incident";
import React from "react";
import { notification } from "../../utils/Notifications.tsx";
import { updateFinalStatus } from "../../services/incident.service.ts";
import { IPayrollReport } from "../../models/Payroll.ts";
import {
  formatPayrollDisplayDate,
  formatPayrollDisplayDateShort,
} from "../../utils/payrollDateFormat.ts";
import {
  resolveIncidentRealChecks,
  resolveScheduledTimesForDisplay,
} from "../../utils/incidentDisplayTimes.ts";
import { isPendingLeaderReviewIncident } from "../../utils/incidentLeaderReviewFilter.ts";
import { getPayrollIncidentBranchName } from "../../utils/incidentBranchLabel.ts";
import { getIncidentRecordBranchId } from "../../utils/incidentBranchMeta.ts";
import type { BranchReportMeta } from "../../utils/payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  partnerShiftFromIncidentSchedule,
} from "../../utils/nightScheduleEligibility.ts";

const cellStyle: React.CSSProperties = {
  padding: "0.4rem 0.45rem",
  verticalAlign: "middle",
  borderBottom: "1px solid #e2e8f0",
  borderRight: "none",
  borderLeft: "none",
  fontSize: "0.78rem",
  color: "#334155",
};

/** Más aire solo en columnas de acción (íconos sin cambiar tamaño). */
const cellStyleActions: React.CSSProperties = {
  ...cellStyle,
  padding: "0.55rem 0.65rem",
  verticalAlign: "middle",
};

const cellWrapStyle: React.CSSProperties = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.35,
};

const cellProgramadoStyle: React.CSSProperties = {
  ...cellStyle,
  backgroundColor: "#ecfdf5",
};

const cellRealStyle: React.CSSProperties = {
  ...cellStyle,
  backgroundColor: "#fffbeb",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
  textAlign: "center",
  verticalAlign: "middle",
};

const realTimeStackStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  minWidth: 0,
  maxWidth: "100%",
  margin: "0 auto",
};

const realTimeLineStyle: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  lineHeight: 1.2,
};

const realDateLineStyle: React.CSSProperties = {
  fontSize: "0.62rem",
  color: "#94a3b8",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  lineHeight: 1.15,
};

const DEFAULT_BRANCH_META: BranchReportMeta = {
  nightScheduleEnabled: false,
  cutoffMinutes: 3 * 60,
};

const RowDetailIncident = ({
  record,
  payroll,
  rowIndex = 0,
  payrollBranchMeta = {},
  onRefreshPartner,
  onIncidentUpdated,
}: {
  record: Incident;
  payroll: IPayrollReport;
  rowIndex?: number;
  payrollBranchMeta?: Record<number, BranchReportMeta>;
  onRefreshPartner?: (partnerId: number) => Promise<void>;
  onIncidentUpdated?: () => Promise<void>;
}) => {
  const branchId = getIncidentRecordBranchId(
    record as Parameters<typeof getIncidentRecordBranchId>[0]
  );
  const partnerShift = partnerShiftFromIncidentSchedule(record?.schedule);
  const effectiveMeta = effectiveNightMetaForPartner(
    payrollBranchMeta[branchId] ?? DEFAULT_BRANCH_META,
    partnerShift
  );
  const nightScheduleEnabled = effectiveMeta.nightScheduleEnabled;
  const [loading, setLoading] = React.useState(false);
  const row = record as Incident & { isUnscheduledPending?: boolean };
  const needsLeaderReview = !row.authorizeBy;
  const isUnscheduledPending = row.isUnscheduledPending === true;
  const isPendingLeader =
    row.isPendingLeaderReview === true || isPendingLeaderReviewIncident(row);
  const incidentId = row.id != null ? String(row.id) : "";
  const hasPersistedIncidentId =
    incidentId.length > 0 &&
    !incidentId.startsWith("no-programado-") &&
    !incidentId.startsWith("pending-review-");
  /** Acción final solo bloqueada si aún no hay revisión del líder o no hay registro en BD. */
  const canSetFinalStatus =
    hasPersistedIncidentId &&
    !needsLeaderReview &&
    !isUnscheduledPending &&
    !isPendingLeader;
  const isRowMuted = needsLeaderReview || isUnscheduledPending || isPendingLeader;
  const resolveFinalStatus = React.useCallback((inc: Incident) => {
    return inc.createdAt === inc.updateAt
      ? inc.type === "Aceptar"
      : inc.finalStatus;
  }, []);

  const [status, setStatus] = React.useState(() => resolveFinalStatus(row));

  React.useEffect(() => {
    setStatus(resolveFinalStatus(row));
  }, [
    row.id,
    row.finalStatus,
    row.updateAt,
    row.createdAt,
    row.type,
    resolveFinalStatus,
  ]);

  const updateIncident = async (value: boolean) => {
    if (!canSetFinalStatus) return;
    setLoading(true);
    setStatus(value);
    try {
      await updateFinalStatus(incidentId, value);

      if (onIncidentUpdated) {
        await onIncidentUpdated();
      }

      if (onRefreshPartner && payroll.partnerId) {
        await onRefreshPartner(payroll.partnerId);
      }
    } catch (error) {
      setStatus(resolveFinalStatus(record));
      notification("Error ⚠️", "Error al actualizar la incidencia", "danger");
    } finally {
      setLoading(false);
    }
  };

  const rowBg =
    loading
      ? "rgba(220, 38, 38, 0.08)"
      : isRowMuted
        ? "#f8fafc"
        : rowIndex % 2 === 0
          ? "#ffffff"
          : "#f8fafc";

  const scheduled = resolveScheduledTimesForDisplay(
    record?.schedule?.startTime,
    record?.schedule?.endTime,
    nightScheduleEnabled
  );
  const { checkIn: realCheckIn, checkOut: realCheckOut } =
    resolveIncidentRealChecks(record, {
      branchMeta: payrollBranchMeta[branchId] ?? DEFAULT_BRANCH_META,
      shiftName: partnerShift,
    });
  const isCrossDayCheck =
    Boolean(realCheckIn?.checkDate) &&
    Boolean(realCheckOut?.checkDate) &&
    String(realCheckIn?.checkDate) !== String(realCheckOut?.checkDate);

  return (
    <tr
      className="text-center"
      style={{
        backgroundColor: rowBg,
        opacity: loading ? 0.65 : 1,
        cursor: loading ? "progress" : "inherit",
        transition: "background-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.backgroundColor = "#fef2f2";
        }
      }}
      onMouseLeave={(e) => {
        if (!loading) {
          e.currentTarget.style.backgroundColor = rowBg;
        }
      }}
    >
      <td style={{ ...cellStyle, fontWeight: 600, color: "#b91c1c" }}>
        {record?.schedule?.exactDate
          ? formatPayrollDisplayDate(
              moment.utc(record.schedule.exactDate).format("YYYY-MM-DD")
            )
          : realCheckIn?.checkDate
            ? formatPayrollDisplayDate(
                moment.utc(realCheckIn.checkDate).format("YYYY-MM-DD")
              )
            : "N/A"}
      </td>
      <td style={cellProgramadoStyle}>{scheduled.entrada}</td>
      <td style={cellProgramadoStyle}>{scheduled.salida}</td>
      <td style={cellRealStyle}>
        <div style={realTimeStackStyle}>
          <span style={realTimeLineStyle}>{realCheckIn?.checkTime}</span>
        </div>
      </td>
      <td style={cellRealStyle}>
        <div style={realTimeStackStyle}>
          <span style={realTimeLineStyle}>{realCheckOut?.checkTime}</span>
          {isCrossDayCheck && realCheckOut?.checkDate ? (
            <span style={realDateLineStyle}>
              {formatPayrollDisplayDateShort(
                moment.utc(realCheckOut.checkDate).format("YYYY-MM-DD")
              )}
            </span>
          ) : null}
        </div>
      </td>
      <td
        className="incidents-td-tipo"
        style={{
          ...cellStyle,
          textAlign: "center",
          verticalAlign: "middle",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.35rem",
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          }}
        >
          <span
            style={{
              display: "block",
              maxWidth: "100%",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {record.status === "Aceptar" ? "No programado" : record.status}
          </span>
          {record.isFreeDayWorked && (
            <span
              className="badge bg-warning text-dark"
              style={{
                fontSize: "0.65rem",
                maxWidth: "100%",
                whiteSpace: "normal",
                textAlign: "center",
                wordBreak: "break-word",
              }}
            >
              <i className="fas fa-calendar-day me-1"></i>
              Día Libre
            </span>
          )}
        </div>
      </td>
      <td style={{ ...cellStyle, textAlign: "left", ...cellWrapStyle }}>
        {getPayrollIncidentBranchName(record)}
      </td>
      <td style={{ ...cellStyle, fontWeight: 600 }}>
        {typeof record.workedTime === "number"
          ? record.workedTime.toFixed(2)
          : record.workedTime}
      </td>
      <td style={{ ...cellStyle, textAlign: "left", ...cellWrapStyle }}>{record.reason}</td>
      <td style={{ ...cellStyle, textAlign: "left", ...cellWrapStyle }}>
        {record.authorizeBy ? (
          toCamelCase(
            `${record.authorizeBy.name} ${record.authorizeBy.lastName} ${record.authorizeBy.secondLastName}`
          )
        ) : (
          <span className="text-muted fst-italic">
            Pendiente de revisión del líder
          </span>
        )}
      </td>
      <td style={{ ...cellStyle, textAlign: "center" }}>
        {record.authorizeBy && record.updateAt ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0.2rem",
              lineHeight: 1.3,
            }}
          >
            <span style={{ fontWeight: 600, color: "#334155", fontSize: "0.72rem" }}>
              {formatPayrollDisplayDate(
                moment.utc(record.updateAt).format("YYYY-MM-DD")
              )}
            </span>
            <span
              style={{
                fontSize: "0.75rem",
                color: "#94a3b8",
                fontWeight: 500,
              }}
            >
              {moment.utc(record.updateAt).format("HH:mm:ss")}
            </span>
          </div>
        ) : (
          "—"
        )}
      </td>
      <td style={cellStyleActions}>
        {needsLeaderReview ? (
          <span
            className="text-muted"
            title="Sin decisión del líder: no implica aprobación ni rechazo"
          >
            —
          </span>
        ) : record.type === "Aceptar" ? (
          <i className="fa-solid fa-circle-check text-success fs-1" />
        ) : (
          <i className="fa-solid fa-circle-xmark text-danger fs-1" />
        )}
      </td>
      <td style={cellStyleActions}>
        {!canSetFinalStatus ? (
          <span
            className="text-muted small"
            title={
              needsLeaderReview || isPendingLeader
                ? "Pendiente de revisión del líder en RH"
                : isUnscheduledPending
                  ? "Primero debe resolverse en incidencias (RH)"
                  : "Sin registro de incidencia en BD"
            }
          >
            <i className="fa-solid fa-lock me-1" />
            Bloqueado
          </span>
        ) : (
          Boolean(
            record.status !== "Salió antes" && record.status !== "Llegó tarde"
          ) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <i
                className="fa-solid fa-circle-check fs-1"
                style={{
                  cursor: "pointer",
                  color: status ? "#33acac" : "rgb(108 117 125 / 68%)",
                }}
                onClick={() => updateIncident(true)}
              />

              <i
                className="fa-solid fa-circle-xmark fs-1"
                style={{
                  cursor: "pointer",
                  color: status ? "rgb(108 117 125 / 68%)" : "#dc3545",
                }}
                onClick={() => updateIncident(false)}
              />
            </div>
          )
        )}
      </td>
    </tr>
  );
};

export default RowDetailIncident;
